import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { merge } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';

import { environment } from '@env/environment';
import { Logger, I18nService, untilDestroyed, RouteReusableStrategy } from '@app/core';
import { onDeviceReady } from '@utils/cordova';
import { AdminConfigService } from './core/admin-config.service';
import { SpinnerService, SPINNER_SIZES } from '@app/shared/spinner';

const log = new Logger('App');

@Component({
  standalone: false,
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  public spinnerShow = false;
  public spinnerFullscreen = true;
  public spinnerSize: SPINNER_SIZES = SPINNER_SIZES.LARGE;
  public color1 = '#57B846';

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private titleService: Title,
    private translateService: TranslateService,
    private i18nService: I18nService,
    private $config: AdminConfigService,
    private spinnerService: SpinnerService
  ) {
    // Ensure reuse strategy stays active across navigation.
    this.router.routeReuseStrategy = new RouteReusableStrategy();
    this.init();
  }

  private async init(): Promise<void> {
    await onDeviceReady();

    /// Init: Database
    try {
      const i18n = this.i18nService.get();
      if (!i18n) {
        this.i18nService.set(null);
      }
    } catch (e) {
      console.error('AppComponent.init()::catch', e);
    }

    /// Init: i18n
    await this.i18nService.init();

    /// Init: AdminConfig
    const pathName = window.location?.pathname || this.router.url || '';
    const isAuthFlow = /\/validate\//.test(pathName) || /\/recovery\//.test(pathName) || /\/login$/.test(pathName);
    if (!isAuthFlow) {
      const bool = await this.$config.onInit();
      if (bool === true) {
        this.router.navigate(['/', 'shell']);
      } else {
        this.router.navigate(['/', 'config-lacks']);
      }
    }
  }

  ngOnInit() {
    // Setup logger
    if (environment.production) {
      Logger.enableProductionMode();
    }

    log.debug('init');

    // Setup translations
    // this.i18nService.init(environment.defaultLanguage, environment.supportedLanguages);

    const onNavigationEnd = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    );

    // Change page title on navigation or language change, based on route data
    merge(this.translateService.onLangChange, onNavigationEnd)
      .pipe(
        map(() => {
          let route = this.activatedRoute;
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        filter(route => route.outlet === 'primary'),
        switchMap(route => route.data),
        untilDestroyed(this)
      )
      .subscribe(event => {
        const title = event.title;
        if (title) {
          this.titleService.setTitle(this.translateService.instant(title));
        }
      });

    this.spinnerService.visible$
      .pipe(untilDestroyed(this))
      .subscribe(visible => {
        this.spinnerShow = visible;
      });
  }

  ngOnDestroy() {
    this.i18nService.destroy();
  }
}
